import { Extension, type Editor } from "@tiptap/core";
import { Plugin } from "@tiptap/pm/state";
import { INDENT_NODE_NAME } from "./textEditModel";

/** Options wiring the session's lifecycle keys and capabilities to the hook. */
export interface TextEditKeymapOptions {
  /** Whether the session root can hold block structure (Enter splits blocks). */
  blocksEnabled: boolean;
  /** Commits the session (Cmd/Ctrl+Enter). */
  onCommit: () => void;
  /** Cancels the session, discarding changes (Escape). */
  onCancel: () => void;
}

/**
 * TipTap extension binding the inline edit session's keys, matching how
 * PandaDoc's editor behaves: Enter starts a new paragraph (or a new item
 * inside a list), Shift+Enter adds a soft line break, Cmd/Ctrl+Enter commits
 * the session, Escape cancels it, and Tab / Shift+Tab nest and un-nest list
 * items or indent and unindent any other line. Enter only commits in sessions
 * whose root cannot hold blocks, where there is nothing to split into. High
 * priority so Enter beats the core split-block binding and Cmd+Enter beats
 * HardBreak's own Mod-Enter binding.
 */
export const TextEditKeymap = Extension.create<TextEditKeymapOptions>({
  name: "textEditKeymap",
  priority: 1000,

  addOptions() {
    return {
      blocksEnabled: true,
      onCommit: () => undefined,
      onCancel: () => undefined,
    };
  },

  addKeyboardShortcuts() {
    return {
      Enter: () => {
        // splitListItem refuses on an empty item and expects a following
        // command to lift out of the list, so chain the lift here - otherwise
        // Enter on an empty bullet does nothing and the list is inescapable.
        if (this.editor.isActive("listItem")) {
          return (
            this.editor.commands.splitListItem("listItem") ||
            this.editor.commands.liftListItem("listItem")
          );
        }
        if (!this.options.blocksEnabled) {
          this.options.onCommit();
          return true;
        }
        return this.editor.commands.splitBlock();
      },
      "Mod-Enter": () => {
        this.options.onCommit();
        return true;
      },
      Escape: () => {
        this.options.onCancel();
        return true;
      },
    };
  },

  // Tab is handled here rather than in addKeyboardShortcuts because it needs
  // the DOM event: the editor renders inside a Radix Dialog, whose FocusScope
  // has a BUBBLING onKeyDown that moves focus to the other end of the dialog
  // when Tab is pressed on the last tabbable element - the edit surface. A
  // ProseMirror keymap only gets to call preventDefault(), which does not stop
  // that handler from running, so Tab used to jump focus out of the surface
  // and blur-commit the session. stopPropagation() is what keeps the caret in
  // the field.
  addProseMirrorPlugins() {
    const { editor } = this;
    return [
      new Plugin({
        props: {
          handleKeyDown: (_view, event) => {
            if (event.key !== "Tab" || event.metaKey || event.ctrlKey) {
              return false;
            }
            event.preventDefault();
            event.stopPropagation();
            shiftIndent(editor, event.shiftKey);
            return true;
          },
        },
      }),
    ];
  },
});

/**
 * One tab stop of plain-text indentation. Non-breaking spaces because markdown
 * has no tab stops: ordinary spaces would collapse away when the committed
 * body is rendered, and four of them at the start of a line would read as a
 * code block.
 */
const TAB_INDENT = "\u00A0\u00A0\u00A0\u00A0";

/** Matches the indentation a Shift+Tab takes back, at most one tab stop. */
const TRAILING_INDENT = /\u00A0+$/;

/**
 * Indents at the caret, the way a word processor does. A list item nests. A
 * caret at the start of any other line pushes that whole line to the next
 * indent level, so a list typed there starts at the indent instead of jumping
 * back to the margin. Mid-line, where there is no line to move, Tab is a plain
 * tab stop. Shift+Tab reverses each of the three.
 * @param editor - The session's TipTap instance
 * @param outdent - True to un-nest or unindent (Shift+Tab), false to indent
 */
function shiftIndent(editor: Editor, outdent: boolean): void {
  if (editor.isActive("listItem")) {
    if (outdent) {
      editor.commands.liftListItem("listItem");
      return;
    }
    editor.commands.sinkListItem("listItem");
    return;
  }
  if (editor.state.selection.$from.parentOffset === 0) {
    shiftLineIndent(editor, outdent);
    return;
  }
  if (outdent) {
    unindentText(editor);
    return;
  }
  editor.commands.insertContent(TAB_INDENT);
}

/**
 * Moves the line at the caret one indent level in or out.
 * @param editor - The session's TipTap instance
 * @param outdent - True to take a level back, false to add one
 */
function shiftLineIndent(editor: Editor, outdent: boolean): void {
  if (outdent) {
    editor.commands.lift(INDENT_NODE_NAME);
    return;
  }
  editor.commands.wrapIn(INDENT_NODE_NAME);
}

/**
 * Deletes up to one tab stop of spaces before the caret.
 * @param editor - The session's TipTap instance
 */
function unindentText(editor: Editor): void {
  const { doc, selection } = editor.state;
  if (!selection.empty) {
    return;
  }
  const { $from } = selection;
  const stopStart = Math.max($from.start(), $from.pos - TAB_INDENT.length);
  const indent = doc
    .textBetween(stopStart, $from.pos)
    .match(TRAILING_INDENT)?.[0];
  if (indent === undefined) {
    return;
  }
  editor.commands.deleteRange({
    from: $from.pos - indent.length,
    to: $from.pos,
  });
}
